import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface MatchingPartsListing {
  id: string;
  title: string;
  price: number | null;
  part_number: string | null;
  item_type: string;
  condition: string | null;
  helicopter_manufacturer: string | null;
  city: string | null;
  country: string | null;
  hero_image: string | null;
}

export const useMatchingPartsListings = (
  requestCategory: string | null,
  requestListingType: string,
  userId: string | null
) => {
  const [matchingListings, setMatchingListings] = useState<MatchingPartsListing[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (userId) {
      fetchMatchingListings();
    }
  }, [requestCategory, requestListingType, userId]);

  const fetchMatchingListings = async () => {
    if (!userId) return;

    try {
      setLoading(true);

      // For parts wanted requests, show sale listings
      const matchingType = requestListingType === 'wanted' ? 'sale' : 'sale';

      const { data, error } = await supabase
        .from('parts_listings')
        .select('id, title, price, part_number, item_type, condition, helicopter_manufacturer, city, country, hero_image')
        .eq('user_id', userId)
        .eq('listing_type', matchingType)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      setMatchingListings(data || []);
    } catch (error) {
      console.error('Error fetching matching parts listings:', error);
      toast({
        title: "Error loading listings",
        description: "Failed to load your matching parts listings.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return { matchingListings, loading, refetch: fetchMatchingListings };
};
